import { ComponentProps, MouseEventHandler, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { I18n } from '@/i18n/I18n';
import { initialsOf, relativeTime, shortName } from '@/frontend/lib/people';
import { cn } from '@/frontend/lib/utils';
import { Button, buttonVariants } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

/** An icon-only button, named by its tooltip for sighted authors and by its aria-label for everyone else. */
export function IconButton({ label, children, variant = 'ghost', size = 'icon-sm', ...props }: ComponentProps<typeof Button> & { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Button variant={variant} size={size} aria-label={label} {...props} />}>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** The same, for a link: it navigates, so it stays an <a> rather than becoming a button that happens to. */
export function IconLink({ label, to, onClick, children }: { label: string; to: string; onClick?: MouseEventHandler<HTMLAnchorElement>; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Link to={to} onClick={onClick} aria-label={label} className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })} />}>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

const TONES = {
  ok: 'bg-emerald-500',
  bad: 'bg-red-500',
  busy: 'bg-amber-500 animate-pulse',
  idle: 'bg-zinc-300',
};

/** The small round light the studio marks a state with: compiles, unsaved, a tool call running. */
export function StatusDot({ tone, className }: { tone: keyof typeof TONES; className?: string }) {
  return <span aria-hidden="true" className={cn('inline-block size-1.5 shrink-0 rounded-full', TONES[tone], className)} />;
}

/** Shown wherever a draft differs from its latest published version: a report, or a schema. */
export function UnpublishedChip({ className }: { className?: string }) {
  return (
    <span title={I18n.get('unpublishedChangesHelp')} className={cn('inline-flex shrink-0 items-center rounded-full border-[1px] border-sky-200 bg-sky-50 px-2 text-xs font-medium whitespace-nowrap text-sky-900', className)}>
      {I18n.get('unpublishedChanges')}
    </span>
  );
}

/** A list's Published column: the latest version and when, or that there is none, and whether the draft has moved on since. */
export function PublishedCell({ version, changed }: { version: { number: number; createdAt?: Date | string } | null; changed: boolean }) {
  if (!version) return <span className="text-muted-foreground">{I18n.get('reportBundleNotPublished')}</span>;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="whitespace-nowrap">v{version.number}{version.createdAt ? <span className="text-muted-foreground"> · {new Date(version.createdAt).toLocaleDateString()}</span> : null}</span>
      {changed && <UnpublishedChip />}
    </span>
  );
}

/** Who last edited something, and how long ago; the exact time and their address on hover. */
export function EditedBy({ at, by }: { at?: Date | string | null; by?: string | null }) {
  if (!at) return null;
  const full = `${new Date(at).toLocaleString()}${by ? ` · ${by}` : ''}`;
  return (
    <span title={full} className="inline-flex items-center gap-2 whitespace-nowrap">
      {by && <span aria-hidden="true" className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[0.65rem] font-semibold text-sky-900">{initialsOf(by)}</span>}
      <span>{relativeTime(at)}</span>
      {by && <span className="text-muted-foreground">{I18n.get('byPerson', { name: shortName(by) })}</span>}
    </span>
  );
}

/** A thin bar heading a panel: its name on the left, its actions on the right. */
export function PanelBar({ title, icon, children, className }: { title: ReactElement | string; icon?: ReactElement; children?: ReactElement | ReactElement[] | false | null; className?: string }) {
  return (
    <div className={cn('flex h-[2.5rem] shrink-0 items-center justify-between gap-2 border-b bg-background px-3', className)}>
      <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
        {icon}
        {title}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">{children}</div>
    </div>
  );
}
